/**
 * Developer Onboarding Workflow
 *
 * This workflow onboards developers by:
 * 1. Retrieving/creating user(s) in current environment
 * 2. Ensuring organization exists (or using existing one)
 * 3. Setting organization configuration
 * 4. Ensuring application exists (or using existing one)
 * 5. Ensuring invite template is deployed to Passport for the app
 * 6. Adding users to "App Developers" group (sends invite email)
 * 7. Adding users to "Enabled Developers" group (optional)
 * 8. Finalizing onboarding
 *
 * The workflow runs in a single environment (PROD or SANDBOX).
 *
 * Business logic is in developer_onboarding_shared.mjs
 * Server uses _userType + _scriptName to find and execute the correct function
 */

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

/**
 * Create the Developer Onboarding Workflow
 *
 * Input: {
 *   permissionProfileId: "abc-123-def" // Optional - if not provided, workflow runs with caller's permissions
 * }
 *
 * Example usage:
 * const workflow = await createDeveloperOnboardingWorkflow({ permissionProfileId: "..." }, libraries, ctx);
 *
 * Then run it via runDeveloperOnboardingWorkflow(...) below, which wraps the
 * input under `_inputParams` (Twinit Workflow Service contract — see
 * https://twinit.dev/docs/concepts/back-end-services/workflow-service and the
 * REST docs for POST /workflowsvc/api/v1/workflows).
 *
 * The top-level `_inputParams` block on the WorkflowDef is REQUIRED for
 * `${workflow.input.X}` template references inside task `_inputParams` to
 * resolve at runtime. Without it, every task receives `undefined` for those
 * fields and silently no-ops.
 *
 * IMPORTANT — schema for the top-level `_inputParams`:
 * Per Twinit docs (concepts/back-end-services/workflow-service →
 * "How to use dynamic inputs in Workflows"), each entry must be a parameter
 * descriptor object of the shape `{ type, value, encrypt }`, NOT a raw value.
 * Sending raw values (e.g. `dryRun: false`) causes the workflow service to
 * reject the create request with HTTP 400.
 */
/**
 * Build the canonical Developer Onboarding WorkflowDef.
 *
 * Used by both `createDeveloperOnboardingWorkflow` and
 * `updateWorkflowPermissionProfile` so the body sent to
 * `IafWorkflowSvc.createWorkflowDef` / `updateWorkflowDef` is always in
 * the create-style shape. The shape returned by `getWorkflowDefs`
 * includes server-assigned fields (`_id`, `_createdAt`, `_updatedAt`,
 * task `_id`s, etc.). Spreading that shape back into an update payload
 * causes the Workflow Service to APPEND the loaded `_taskDefs` rather
 * than replace them — every "update permission profile" call would
 * therefore duplicate the entire task list, which then materialises as
 * duplicate `_tasks` (and parallel runs of every step) in subsequent
 * workflow runs.
 */
function buildDeveloperOnboardingWorkflowDef(ctx, { permissionProfileId } = {}) {
    const workflowDef = {
        _name: "Developer_Onboarding_Workflow",
        _description: "Onboards developers by setting up organization, application, and user groups in the current environment",
        _namespaces: ctx._namespaces,
        _userType: "developer_onboarding_workflow",
        // _timeoutPolicy intentionally omitted: the Twinit docs are
        // inconsistent about valid values ("TIMED_OUT_WF" vs "TIME_OUT_WF")
        // and the workflow service's schema validator rejects unknown enum
        // values with HTTP 400. Letting the service apply its default avoids
        // the issue. Re-enable only after confirming the accepted enum from
        // your platform deployment.
        // _timeoutPolicy: "TIME_OUT_WF",
        _timeoutSeconds: 600,
        // Top-level inputs the workflow accepts. Each key listed here can be
        // referenced from task _inputParams as `${workflow.input.<key>}`.
        // Per Twinit docs each entry must be a `{ type, value, encrypt }`
        // descriptor (NOT a raw value) — see the "How to use dynamic inputs in
        // Workflows" page. `value` is the default applied when the runtime
        // run request does not supply that key.
        _inputParams: {
            // Organization
            organizationId: { type: "string", value: "" },
            organizationName: { type: "string", value: "" },
            organizationShortName: { type: "string", value: "" },
            // Application
            applicationId: { type: "string", value: "" },
            applicationName: { type: "string", value: "" },
            applicationShortName: { type: "string", value: "" },
            // Users
            adminEmail: { type: "string", value: "" },
            developerEmails: { type: "array", value: [] },
            // Options
            dryRun: { type: "boolean", value: false },
            // Invite email customization (Passport USER_GROUP_INVITE template _params)
            inviteLink: { type: "string", value: "" },
            inviterName: { type: "string", value: "" },
            bodyContent: { type: "string", value: "" },
            // Skip flags
            skipRetrieveUser: { type: "boolean", value: false },
            skipEnsureOrganization: { type: "boolean", value: false },
            skipSetOrgConfig: { type: "boolean", value: false },
            skipEnsureApp: { type: "boolean", value: false },
            skipAddToAppDevs: { type: "boolean", value: false },
            skipAddToEnabledDevs: { type: "boolean", value: false },
            // Privileged Console App token for Passport admin operations (org/app
            // creation and org config). Must be encrypted so it is never stored
            // in plain text in the workflow run record.
            consoleAuthToken: { type: "string", value: "", encrypt: true },
            // ID of the management app (this UI's app) — needed to whitelist it
            // in newly created apps so it can deploy notification templates.
            managementAppId: { type: "string", value: "" },
            // Email of the user who triggered the workflow — becomes the app _appOwner.
            currentUserEmail: { type: "string", value: "" }
        },
        _taskDefs: [
            // Step 1: Retrieve/Create User(s)
            {
                _type: "SCRIPT_EXECUTION",
                _name: "retrieve_user",
                _sequenceno: 1,
                _retryCount: 2,
                _timeoutSeconds: 120,
                _inputParams: {
                    // Organization params
                    "organizationId": "${workflow.input.organizationId}",
                    "organizationName": "${workflow.input.organizationName}",
                    "organizationShortName": "${workflow.input.organizationShortName}",
                    // Application params
                    "applicationId": "${workflow.input.applicationId}",
                    "applicationName": "${workflow.input.applicationName}",
                    "applicationShortName": "${workflow.input.applicationShortName}",
                    // User params
                    "adminEmail": "${workflow.input.adminEmail}",
                    "developerEmails": "${workflow.input.developerEmails}",
                    // Options
                    "dryRun": "${workflow.input.dryRun}",
                    // Skip flags
                    "skipRetrieveUser": "${workflow.input.skipRetrieveUser}",
                    "skipEnsureOrganization": "${workflow.input.skipEnsureOrganization}",
                    "skipSetOrgConfig": "${workflow.input.skipSetOrgConfig}",
                    "skipEnsureApp": "${workflow.input.skipEnsureApp}",
                    "skipAddToAppDevs": "${workflow.input.skipAddToAppDevs}",
                    "skipAddToEnabledDevs": "${workflow.input.skipAddToEnabledDevs}",
                    _userType: "onboarding_shared",
                    _scriptName: "retrieveUser"
                }
            },
            // Step 2: Ensure Organization
            {
                _type: "SCRIPT_EXECUTION",
                _name: "ensure_organization",
                _sequenceno: 2,
                _retryCount: 2,
                _timeoutSeconds: 90,
                _inputParams: {
                    "userTaskOutput": "${retrieve_user._output}",
                    "organizationId": "${workflow.input.organizationId}",
                    "organizationName": "${workflow.input.organizationName}",
                    "organizationShortName": "${workflow.input.organizationShortName}",
                    "adminEmail": "${workflow.input.adminEmail}",
                    "dryRun": "${workflow.input.dryRun}",
                    "skipEnsureOrganization": "${workflow.input.skipEnsureOrganization}",
                    "consoleAuthToken": "${workflow.input.consoleAuthToken}",
                    _userType: "onboarding_shared",
                    _scriptName: "ensureOrganization"
                }
            },
            // Step 3: Set Organization Config
            {
                _type: "SCRIPT_EXECUTION",
                _name: "set_org_config",
                _sequenceno: 3,
                _retryCount: 2,
                _timeoutSeconds: 60,
                _inputParams: {
                    "organizationTaskOutput": "${ensure_organization._output}",
                    "dryRun": "${workflow.input.dryRun}",
                    "skipSetOrgConfig": "${workflow.input.skipSetOrgConfig}",
                    "consoleAuthToken": "${workflow.input.consoleAuthToken}",
                    _userType: "onboarding_shared",
                    _scriptName: "setOrganizationConfig"
                }
            },
            // Step 4: Ensure Application (follows naming rules)
            {
                _type: "SCRIPT_EXECUTION",
                _name: "ensure_training_app",
                _sequenceno: 4,
                _retryCount: 2,
                _timeoutSeconds: 90,
                _inputParams: {
                    "organizationTaskOutput": "${ensure_organization._output}",
                    "applicationId": "${workflow.input.applicationId}",
                    "applicationName": "${workflow.input.applicationName}",
                    "applicationShortName": "${workflow.input.applicationShortName}",
                    "adminEmail": "${workflow.input.adminEmail}",
                    "dryRun": "${workflow.input.dryRun}",
                    "skipEnsureApp": "${workflow.input.skipEnsureApp}",
                    "consoleAuthToken": "${workflow.input.consoleAuthToken}",
                    "managementAppId": "${workflow.input.managementAppId}",
                    "currentUserEmail": "${workflow.input.currentUserEmail}",
                    _userType: "onboarding_shared",
                    _scriptName: "ensureApplication"
                }
            },
            // Step 5: Ensure invite template is deployed to Passport for this app
            {
                _type: "SCRIPT_EXECUTION",
                _name: "ensure_invite_template",
                _sequenceno: 5,
                _retryCount: 2,
                _timeoutSeconds: 90,
                _inputParams: {
                    "applicationTaskOutput": "${ensure_training_app._output}",
                    "dryRun": "${workflow.input.dryRun}",
                    "consoleAuthToken": "${workflow.input.consoleAuthToken}",
                    "managementAppId": "${workflow.input.managementAppId}",
                    _userType: "onboarding_shared",
                    _scriptName: "ensureInviteTemplate"
                }
            },
            // Step 6: Invite Users to App Developers (sends invite email via USER_GROUP_INVITE template)
            {
                _type: "SCRIPT_EXECUTION",
                _name: "add_user_to_app_devs",
                _sequenceno: 6,
                _retryCount: 2,
                _timeoutSeconds: 120,
                _inputParams: {
                    "userTaskOutput": "${retrieve_user._output}",
                    "applicationTaskOutput": "${ensure_training_app._output}",
                    "adminEmail": "${workflow.input.adminEmail}",
                    "developerEmails": "${workflow.input.developerEmails}",
                    "dryRun": "${workflow.input.dryRun}",
                    "skipAddToAppDevs": "${workflow.input.skipAddToAppDevs}",
                    "consoleAuthToken": "${workflow.input.consoleAuthToken}",
                    // Invite email params (body_content etc. on USER_GROUP_INVITE)
                    "inviteLink": "${workflow.input.inviteLink}",
                    "inviterName": "${workflow.input.inviterName}",
                    "bodyContent": "${workflow.input.bodyContent}",
                    _userType: "onboarding_shared",
                    _scriptName: "addUsersToAppDevelopers"
                }
            },
            // Step 7: Add Users to Enabled Developers
            {
                _type: "SCRIPT_EXECUTION",
                _name: "add_user_to_enabled_devs",
                _sequenceno: 7,
                _retryCount: 2,
                _timeoutSeconds: 120,
                _inputParams: {
                    "userTaskOutput": "${retrieve_user._output}",
                    "adminEmail": "${workflow.input.adminEmail}",
                    "developerEmails": "${workflow.input.developerEmails}",
                    "dryRun": "${workflow.input.dryRun}",
                    "skipAddToEnabledDevs": "${workflow.input.skipAddToEnabledDevs}",
                    "consoleAuthToken": "${workflow.input.consoleAuthToken}",
                    _userType: "onboarding_shared",
                    _scriptName: "addUsersToEnabledDevelopers"
                }
            },
            // Step 8: Finalize Onboarding
            {
                _type: "SCRIPT_EXECUTION",
                _name: "finalize_onboarding",
                _sequenceno: 8,
                _retryCount: 1,
                _timeoutSeconds: 60,
                _inputParams: {
                    "userTaskOutput": "${retrieve_user._output}",
                    "organizationTaskOutput": "${ensure_organization._output}",
                    "applicationTaskOutput": "${ensure_training_app._output}",
                    "inviteTemplateTaskOutput": "${ensure_invite_template._output}",
                    "appDevsTaskOutput": "${add_user_to_app_devs._output}",
                    "enabledDevsTaskOutput": "${add_user_to_enabled_devs._output}",
                    "adminEmail": "${workflow.input.adminEmail}",
                    "developerEmails": "${workflow.input.developerEmails}",
                    "dryRun": "${workflow.input.dryRun}",
                    _userType: "onboarding_shared",
                    _scriptName: "finalizeOnboarding"
                }
            }
        ]
    };

    // NOTE on permission profiles:
    // The Workflow Service uses CAMEL-CASE `_permissionProfileId` on the
    // WorkflowDef body. This differs from the Datasources Service
    // orchestrator field, which is the all-lowercase `_permissionprofileid`.
    // Using the wrong casing causes the workflow service's schema validator
    // to reject the request with HTTP 400.
    if (permissionProfileId) {
        workflowDef._permissionProfileId = permissionProfileId;
    }

    return workflowDef;
}

async function createDeveloperOnboardingWorkflow(input, libraries, ctx) {
    const { IafWorkflowSvc } = libraries.PlatformApi;

    const { permissionProfileId } = input || {};

    // Always start from a clean slate: delete any existing workflow defs
    // (and their runs, where supported) for this _userType. Without this,
    // re-running "Create" would leave the prior def behind and any new
    // call to `updateWorkflowDef` could append rather than replace tasks
    // (see the duplicated _taskDefs bug we hit previously).
    let deletedExisting = [];
    try {
        const existing = await IafWorkflowSvc.getWorkflowDefs({
            query: { _userType: "developer_onboarding_workflow" }
        }, ctx);

        for (const wf of existing?._list || []) {
            if (!wf._id) continue;
            try {
                await IafWorkflowSvc.deleteWorkflowDef(wf._id, ctx);
                deletedExisting.push({ workflowId: wf._id, workflowName: wf._name });
            } catch (delErr) {
                console.warn(
                    `Could not delete existing workflow def ${wf._id} (${wf._name}):`,
                    delErr?.errorResult?.errorMessage || delErr?.message || delErr
                );
            }
        }
    } catch (lookupErr) {
        console.warn(
            "Could not look up existing developer_onboarding_workflow defs before create:",
            lookupErr?.errorResult?.errorMessage || lookupErr?.message || lookupErr
        );
    }

    const workflowDef = buildDeveloperOnboardingWorkflowDef(ctx, { permissionProfileId });

    let result;
    try {
        result = await IafWorkflowSvc.createWorkflowDef(workflowDef, ctx);
    } catch (err) {
        // Surface the server's actual error message so callers can see why
        // the validator rejected the body (otherwise this is opaque "400").
        const serverMsg = err?.errorResult?.errorMessage
            || err?.message
            || JSON.stringify(err);
        throw new Error(`createWorkflowDef failed: ${serverMsg}`);
    }

    return {
        ...result,
        deletedExisting
    };
}

/**
 * Run the Developer Onboarding Workflow
 *
 * Input: {
 *   workflowUserType: "developer_onboarding_workflow",
 *   
 *   // Organization - either select existing or create new
 *   organizationId: "existing-org-id",        // Optional - if provided, uses existing org
 *   organizationName: "Acme Corp",            // Required if creating new org
 *   organizationShortName: "acme_corp",       // Optional for new org
 *   
 *   // Application - either select existing or create new
 *   applicationId: "existing-app-id",         // Optional - if provided, uses existing app
 *   applicationName: "Training Application",  // Required if creating new app
 *   applicationShortName: "training_app",     // Required if creating new app
 *   
 *   // Users
 *   adminEmail: "admin@acmecorp.com",         // Required if creating org/app
 *   developerEmails: ["dev1@acmecorp.com"],   // Optional - additional developers to add
 *   
 *   // Options
 *   dryRun: false,
 *   addToAppOwner: false,
 *   
 *   // Skip flags
 *   skipRetrieveUser: false,
 *   skipEnsureOrganization: false,
 *   skipSetOrgConfig: false,
 *   skipEnsureApp: false,
 *   skipAddToAppDevs: false,
 *   skipAddToEnabledDevs: false
 * }
 */
async function runDeveloperOnboardingWorkflow(input, libraries, ctx) {
    const { IafWorkflowSvc } = libraries.PlatformApi;

    const {
        workflowUserType,
        // Organization
        organizationId,
        organizationName,
        organizationShortName = "",
        // Application
        applicationId,
        applicationName,
        applicationShortName,
        // Users
        adminEmail,
        developerEmails = [],
        // Options
        dryRun = false,
        addToAppOwner = false,
        // Invite email customization
        inviteLink = "",
        inviterName = "",
        bodyContent = "",
        // Skip flags
        skipRetrieveUser = false,
        skipEnsureOrganization = false,
        skipSetOrgConfig = false,
        skipEnsureApp = false,
        skipAddToAppDevs = false,
        skipAddToEnabledDevs = false,
        // Privileged token for Passport admin operations (org/app creation, org config)
        consoleAuthToken,
        // ID of the management app — needed to whitelist it in newly created apps
        managementAppId,
        // Email of the user who triggered the workflow — becomes the app _appOwner
        currentUserEmail
    } = input;

    // Find the workflow by userType
    const workflows = await IafWorkflowSvc.getWorkflowDefs({
        query: { _userType: workflowUserType || "developer_onboarding_workflow" }
    }, ctx);

    if (!workflows._list || workflows._list.length === 0) {
        return {
            success: false,
            message: "Workflow not found. Please create it first using createDeveloperOnboardingWorkflow."
        };
    }

    const workflow = workflows._list[0];

    // Per Twinit Workflow Service REST contract for POST /workflowsvc/api/v1/workflows,
    // the body MUST include `_workflowDefId` and `_inputParams`. NB: although
    // `IafWorkflowSvc.runWorkflow(workflowDefId, params, ctx)` takes a
    // `workflowDefId` as its first argument, the SDK currently DROPS it (it
    // is neither appended to the URL nor added to the body — see
    // `iaf-workflow-svc.js`). Without `_workflowDefId` on the payload the
    // workflow service responds with HTTP 400
    // {"message":"Workflow Definition ID cannot be null or empty"}.
    //
    // The keys inside `_inputParams` must match the WorkflowDef's top-level
    // `_inputParams` declaration so that template references inside task
    // _inputParams (e.g. `${workflow.input.dryRun}`) resolve correctly.
    const runRequest = {
        _workflowDefId: workflow._id,
        _inputParams: {
            // Organization
            organizationId,
            organizationName,
            organizationShortName,
            // Application
            applicationId,
            applicationName,
            applicationShortName,
            // Users
            adminEmail,
            developerEmails,
            // Options
            dryRun,
            addToAppOwner,
            // Invite email customization
            inviteLink,
            inviterName,
            bodyContent,
            // Skip flags
            skipRetrieveUser,
            skipEnsureOrganization,
            skipSetOrgConfig,
            skipEnsureApp,
            skipAddToAppDevs,
            skipAddToEnabledDevs,
            // Privileged token for Passport admin operations
            consoleAuthToken,
            // ID of the management app
            managementAppId,
            // Email of the workflow runner — becomes app _appOwner
            currentUserEmail
        }
    };

    const runResult = await IafWorkflowSvc.runWorkflow(workflow._id, runRequest, ctx);

    return {
        success: true,
        workflowId: workflow._id,
        runId: runResult._id,
        status: runResult._status,
        message: "Developer onboarding workflow started successfully"
    };
}

/**
 * Update permission profile attachment on existing workflow definition.
 * Mirrors updateOrchestratorPermissionProfile in developer_onboarding_orchestrator.mjs.
 *
 * Input: {
 *   permissionProfileId: "abc-123-def" // Required - the profile ID to attach
 * }
 */
async function updateWorkflowPermissionProfile(input, libraries, ctx) {
    const { IafWorkflowSvc } = libraries.PlatformApi;

    const { permissionProfileId } = input || {};

    if (permissionProfileId === undefined) {
        return {
            success: false,
            error: "permissionProfileId is required (pass null to remove)"
        };
    }

    const workflows = await IafWorkflowSvc.getWorkflowDefs({
        query: { _userType: "developer_onboarding_workflow" }
    }, ctx);

    if (!workflows?._list?.length) {
        return {
            success: false,
            error: "Workflow not found. Please create it first."
        };
    }

    const workflow = workflows._list[0];
    // Workflow Service uses camel-case `_permissionProfileId` (different from
    // the orchestrator's all-lowercase `_permissionprofileid`).
    const previousProfileId = workflow._permissionProfileId || null;

    // Do NOT spread `workflow` into the update payload — the loaded shape
    // includes server-assigned `_taskDefs` (each with its own `_id`),
    // `_createdAt`, etc. Sending those back causes the workflow service
    // to APPEND the tasks instead of replacing them, producing duplicated
    // tasks per `_sequenceno` (and parallel runs of every step).
    // Always rebuild the canonical create-style def and overwrite.
    const workflowDef = buildDeveloperOnboardingWorkflowDef(ctx, { permissionProfileId });

    let updated;
    try {
        updated = await IafWorkflowSvc.updateWorkflowDef(workflow._id, workflowDef, ctx);
    } catch (err) {
        const serverMsg = err?.errorResult?.errorMessage
            || err?.message
            || JSON.stringify(err);
        return {
            success: false,
            workflowId: workflow._id,
            workflowName: workflow._name,
            error: `Failed to attach permission profile to workflow def: ${serverMsg}`
        };
    }

    return {
        success: true,
        workflowId: workflow._id,
        workflowName: workflow._name,
        permissionProfileId,
        previousProfileId,
        message: previousProfileId
            ? "Permission profile updated successfully"
            : "Permission profile attached successfully",
        result: updated
    };
}

/**
 * Delete the Developer Onboarding Workflow
 */
async function deleteDeveloperOnboardingWorkflow(input, libraries, ctx) {
    const { IafWorkflowSvc } = libraries.PlatformApi;

    const workflows = await IafWorkflowSvc.getWorkflowDefs({
        query: { _userType: "developer_onboarding_workflow" }
    }, ctx);

    const results = [];
    for (const workflow of workflows._list || []) {
        if (workflow._id) {
            await IafWorkflowSvc.deleteWorkflowDef(workflow._id, ctx);
            results.push({
                deleted: true,
                workflowId: workflow._id,
                workflowName: workflow._name
            });
        }
    }

    return results;
}

/**
 * Get workflow run status and results
 */
async function getDeveloperOnboardingWorkflowResult(input, libraries, ctx) {
    const { IafWorkflowSvc } = libraries.PlatformApi;

    const { workflowRunId } = input;

    const status = await IafWorkflowSvc.getWorkflowRunStatus(workflowRunId, ctx);

    return {
        workflowRunId: workflowRunId,
        status: status._status,
        result: status
    };
}

export {
    createDeveloperOnboardingWorkflow,
    runDeveloperOnboardingWorkflow,
    updateWorkflowPermissionProfile,
    deleteDeveloperOnboardingWorkflow,
    getDeveloperOnboardingWorkflowResult
};
